import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { TaskDetailDialog } from 'shared/dialogs/task-detail';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { Paths } from 'shared/routes/paths';
import { ID } from 'shared/types/id';
import { CreateTask, Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { TaskType } from 'shared/types/task-base';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { targetIsInput } from 'shared/utils/target-is-input';
import { MetaViewOptions } from 'shared/views/task-detail';
import { useTheme } from 'styled-components';

import { DiscardChangesDialog } from './discard-changes-dialog';
import * as Styled from './task-detail.style';
import {
  TaskDetailActionsContext,
  TaskDetailActionsContextProps,
  TaskDetailContext,
  TaskDetailContextProps,
} from './task-detail-context';

export type TaskDetailProviderProps = {
  children: React.ReactNode;
};

export const TaskDetailProvider: React.FC<TaskDetailProviderProps> = ({
  children,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const activePath = useActivePath({ level: 2 });
  const { id: activePathId } = useParams();

  const closeTimerRef = useRef<number>();
  const hasUnsavedCreateContentRef = useRef(false);
  const [initialValues, setInitialValues] = useState<
    Partial<CreateTask> | undefined
  >(undefined);
  const [dateAnchored, setDateAnchored] = useState(false);
  const [activeTask, setActiveTask] = useState<ID>();
  const [initialView, setInitialView] = useState(MetaViewOptions.Settings);
  const [isDiscardDialogOpen, setDiscardDialogOpen] = useState(false);

  const openTask = useCallback(
    (task: Task | undefined, view?: MetaViewOptions) => {
      if (task?.action?.type === TaskActionType.GoalTimeslot) {
        navigate(
          replaceUrlParams(Paths.GoalDetail, { id: task.action.meta.id }),
        );
        return;
      }

      if (task?.action?.type === TaskActionType.LifeAreaTimeslot) {
        navigate(
          replaceUrlParams(Paths.LifeAreaDetail, { id: task.action.meta.id }),
        );
        return;
      }

      closeTimerRef.current && window.clearTimeout(closeTimerRef.current);
      setActiveTask(task?.id);
      setInitialView(
        view ??
          (task?.type === TaskType.Habit
            ? MetaViewOptions.Statistics
            : MetaViewOptions.Settings),
      );
      setInitialValues(undefined);
      setDateAnchored(false);

      if (theme.isMobile) {
        document.body.style.overflow = task ? 'hidden' : '';
      }
    },
    [navigate, theme.isMobile],
  );

  const openTaskStatistics = useCallback(
    (task: Task | undefined) => {
      if (task?.action?.type === TaskActionType.GoalTimeslot) {
        navigate(
          replaceUrlParams(Paths.GoalDetail, { id: task.action.meta.id }),
        );
        return;
      }

      if (task?.action?.type === TaskActionType.LifeAreaTimeslot) {
        navigate(
          replaceUrlParams(Paths.LifeAreaDetail, { id: task.action.meta.id }),
        );
        return;
      }

      closeTimerRef.current && window.clearTimeout(closeTimerRef.current);
      setActiveTask(task?.id);
      setInitialValues(undefined);
      setDateAnchored(false);
    },
    [navigate],
  );

  const openCreateTask = useCallback(
    (
      initialValues?: Partial<CreateTask>,
      options?: { dateAnchored?: boolean },
    ) => {
      setInitialValues(initialValues ?? {});
      setDateAnchored(!!options?.dateAnchored);
      setActiveTask(undefined);
    },
    [],
  );

  const performClose = useCallback(() => {
    hasUnsavedCreateContentRef.current = false;
    setActiveTask(undefined);
    setInitialValues(undefined);
    setDateAnchored(false);
    document.body.style.overflow = '';
  }, []);

  const closeDetail = useCallback(() => {
    if (hasUnsavedCreateContentRef.current) {
      setDiscardDialogOpen(true);
      return;
    }
    performClose();
  }, [performClose]);

  const cancelDiscard = useCallback(() => {
    setDiscardDialogOpen(false);
  }, []);

  const confirmDiscard = useCallback(() => {
    setDiscardDialogOpen(false);
    performClose();
  }, [performClose]);

  const setHasUnsavedCreateContent = useCallback((has: boolean) => {
    hasUnsavedCreateContentRef.current = has;
  }, []);

  const openCreateFromShortcut = useCallback(() => {
    const initialProps: Partial<CreateTask> = {};

    switch (activePath) {
      // case Paths.Home:
      //   initialProps.endStrategy = { deadline: today, completionCount: null };
      //   break;
      case Paths.GoalDetail:
        initialProps.goalId = activePathId as ID;
        break;
      case Paths.LifeAreaDetail:
        initialProps.lifeAreaId = activePathId as ID;
        break;
      case Paths.TagDetail:
        initialProps.tagIds = [activePathId as ID];
        break;
    }

    openCreateTask(initialProps);
  }, [activePath, activePathId, openCreateTask]);

  const [isSubmenuOpen, setSubmenuOpen] = useState(false);

  const actionsValue = useMemo<TaskDetailActionsContextProps>(
    () => ({
      openTaskDetail: openTask,
      openTaskStatistics: openTaskStatistics,
      openCreateTask,
      closeDetail,
    }),
    [closeDetail, openCreateTask, openTask, openTaskStatistics],
  );

  const value = useMemo<TaskDetailContextProps>(
    () => ({
      activeTaskId: activeTask,
      isSubmenuOpen,
      setSubmenuOpen,
      setHasUnsavedCreateContent,
    }),
    [activeTask, isSubmenuOpen, setHasUnsavedCreateContent],
  );

  useModifierKeysDown('KeyN', (e) => {
    if (!window.electron || targetIsInput(e.target)) {
      return; // Don't do a thing
    }

    openCreateFromShortcut();
    e.preventDefault();
  });

  useKeysDown(['KeyN'], (e) => {
    if (window.electron || targetIsInput(e.target)) {
      return; // Don't do a thing
    }

    openCreateFromShortcut();
    e.preventDefault();
  });

  return (
    <TaskDetailActionsContext.Provider value={actionsValue}>
      <TaskDetailContext.Provider value={value}>
        <Styled.Container>
          {children}

          <TaskDetailDialog
            taskId={activeTask}
            onClose={closeDetail}
            initialValues={initialValues}
            dateAnchored={dateAnchored}
            openOnView={initialView}
          />
          <DiscardChangesDialog
            open={isDiscardDialogOpen}
            onClose={cancelDiscard}
            onConfirm={confirmDiscard}
          />
        </Styled.Container>
      </TaskDetailContext.Provider>
    </TaskDetailActionsContext.Provider>
  );
};
